import { useCallback } from 'react';

import {
  enrichResourceFileInfo,
  getContentTypeTag,
  getResourceContentErrorMessage,
  RESOURCE_FILE_UPLOAD_ERROR_CODE,
} from 'src/data/performance';
import { useCustomerActions, usePerformanceActions } from 'src/store';

import { useCipEvents } from './useCipEvents';

export const useUploadResourceFile = () => {
  const customerActions = useCustomerActions();
  const performanceActions = usePerformanceActions();
  const cipEvents = useCipEvents();

  return useCallback(
    async ({ resourceType, file }) => {
      // Check if we already have this file uploaded as a "resource"
      const existenceResult = await performanceActions.validateResourcesExistence({
        file,
        type: resourceType,
      });

      // If we already have a resource (with "id") with the same file, we should try using it
      const firstResult = existenceResult?.[0];
      if (firstResult?.exist) {
        // Check if the existing resource is associated with the current workspace
        const resource = performanceActions.getResource(firstResult.id);
        if (!resource) {
          throw new Error(
            getResourceContentErrorMessage(
              resourceType,
              RESOURCE_FILE_UPLOAD_ERROR_CODE.fileExistsButNotAssociatedToWorkspace
            )
          );
        }

        return resource;
      }

      // If we don't have this file uploaded as a "resource", we should upload it
      const enrichedFile = await enrichResourceFileInfo({ resourceType, file });
      const extractedResource = await performanceActions.extractResource({
        name: enrichedFile.name,
        tags: [getContentTypeTag(resourceType)],
        file: enrichedFile,
        contentType: resourceType,
      });

      // Refresh the customer feature limit and track "add resource" event if an actual resource was extracted
      customerActions.refreshCurrentCustomerFeatureLimit();
      if (extractedResource) {
        cipEvents.trackAddResource();
      }

      return extractedResource;
    },
    [cipEvents, customerActions, performanceActions]
  );
};
